/**
 * Value Formatters
 *
 * Maps the `display.formatter` string from FieldTypePresentationProvider to
 * pure formatting functions. Each formatter takes a raw value and returns
 * a display-ready string (or the raw value for component-rendered types).
 *
 * Formatters are used by both `renderTableCell` (string output) and
 * `renderDetailValue` (as formatted input to value components).
 */

import { formatDateOnly, formatTimeOnly, formatSubmissionDate } from '../../utils/dateUtils';
import {
  parseCheckboxGroup,
  parseTagList,
  parseCoordinates,
  resolveOptionLabel,
  resolveOptionLabels,
} from './parsers';

/**
 * @typedef {Object} FormatContext
 * @property {string} [timezone] - Timezone ID for date formatting
 * @property {Array<{value: string, label: string}>} [options] - Field options for select/radio/checkbox
 * @property {Object} [display] - Display config from registry (prefix, suffix, decimalPlaces, emptyValue)
 * @property {string} [precision] - Date precision override ('date', 'time', 'datetime')
 */

function formatNumber(value, decimalPlaces, prefix, suffix) {
  const num = Number(value);
  if (isNaN(num)) return String(value ?? '');
  const formatted = decimalPlaces != null ? num.toFixed(decimalPlaces) : String(num);
  return `${prefix || ''}${formatted}${suffix || ''}`;
}

function formatCoordinatesText(value) {
  const coords = parseCoordinates(value);
  if (!coords) return null;
  return `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`;
}

/**
 * Registry of formatter functions keyed by the `display.formatter` string.
 * Each function: (value, context) → string | null
 * Returns null to signal "use emptyValue".
 */
const FORMATTERS = {
  string: (value) => {
    if (value == null || value === '') return null;
    return String(value);
  },

  number: (value, ctx) => {
    if (value == null || value === '') return null;
    const { decimalPlaces, prefix, suffix } = ctx.display || {};
    return formatNumber(value, decimalPlaces, prefix, suffix);
  },

  currency: (value, ctx) => {
    if (value == null || value === '') return null;
    const { decimalPlaces = 2, prefix = '$' } = ctx.display || {};
    return formatNumber(value, decimalPlaces, prefix, '');
  },

  percentage: (value, ctx) => {
    if (value == null || value === '') return null;
    const { decimalPlaces = 1, suffix = '%' } = ctx.display || {};
    return formatNumber(value, decimalPlaces, '', suffix);
  },

  date: (value, ctx) => {
    if (!value) return null;
    const precision = ctx.precision;
    if (precision === 'time') return formatTimeOnly(value, ctx.timezone);
    if (precision === 'datetime') return formatSubmissionDate(value, ctx.timezone);
    return formatDateOnly(value, ctx.timezone);
  },

  time: (value, ctx) => {
    if (!value) return null;
    return formatTimeOnly(value, ctx.timezone);
  },

  datetime: (value, ctx) => {
    if (!value) return null;
    const precision = ctx.precision;
    if (precision === 'date') return formatDateOnly(value, ctx.timezone);
    if (precision === 'time') return formatTimeOnly(value, ctx.timezone);
    return formatSubmissionDate(value, ctx.timezone);
  },

  boolean: (value) => {
    if (value == null) return null;
    return value === true || value === 'true' || value === '1' ? 'Yes' : 'No';
  },

  optionLabel: (value, ctx) => {
    if (value == null || value === '') return null;
    return resolveOptionLabel(value, ctx.options);
  },

  optionLabels: (value, ctx) => {
    const labels = resolveOptionLabels(value, ctx.options);
    if (labels.length === 0) return null;
    return labels.join(', ');
  },

  tagList: (value) => {
    const tags = parseTagList(value);
    if (tags.length === 0) return null;
    return tags.join(', ');
  },

  coordinates: (value) => {
    return formatCoordinatesText(value);
  },

  rating: (value, ctx) => {
    if (value == null || value === '') return null;
    const { suffix = '' } = ctx.display || {};
    return `${value}${suffix}`;
  },

  // Component-rendered types — formatter returns raw value, component does the work
  audio: (value) => value || null,
  imageList: (value) => value || null,
  image: (value) => value || null,
  fileList: (value) => value || null,
  subformList: (value) => value || null,
  none: () => null,
};

/**
 * Format a field value using the registry's formatter specification.
 * @param {string} formatterName - The formatter key from registry display config
 * @param {*} value - Raw field value
 * @param {FormatContext} context - Formatting context
 * @returns {string|null} Formatted string or null for empty
 */
export function formatFieldValue(formatterName, value, context = {}) {
  const formatter = FORMATTERS[formatterName] || FORMATTERS.string;
  return formatter(value, context);
}

/**
 * Check if a formatter produces text output (vs raw value for component rendering).
 */
export function isTextFormatter(formatterName) {
  const componentFormatters = ['audio', 'imageList', 'image', 'fileList', 'subformList', 'none'];
  return !componentFormatters.includes(formatterName);
}

export { FORMATTERS };
